import axios from 'axios'
import Vue from 'vue'
import Vuex from 'vuex'
import auth from '@/auth'
import * as Sentry from '@sentry/browser'
import moment from 'moment'

Vue.use(Vuex)

function roundToTwo (n) {
  let multiplicator = Math.pow(10, 2)
  n = parseFloat((n * multiplicator).toFixed(11))
  let final = (Math.round(n) / multiplicator)
  return +(final.toFixed(2))
}

const store = new Vuex.Store({
  state: {
    additionalDays: 0,
    showAdditionalDays: true,
    loadingCreatorData: true,
    loadingUserData: true,
    sendingOrder: false,
    token: '',
    step: 1,
    steps: 4,
    plans: [],
    trainings: [],
    others: [],
    selectedPlanIndex: null,
    selectedPeriodIndex: null,
    selectedPackageId: null,
    selectedExtrasIds: [],
    user: {
      userFirstName: '',
      userLastName: '',
      email: '',
      planName: '',
      packageUnusedAmount: null,
      currentPeriodId: null,
      currentPlanId: null,
      trainings: [],
      isVip: false,
      avatar: false
    },
    invoice: false,
    vatIdNotRequired: false,
    form: {
      clientFirstName: '',
      clientLastName: '',
      clientPhone: '',
      note: '',
      documentName: '',
      documentStreet: '',
      documentPostalCode: '',
      documentCity: '',
      documentVatId: '',
      code: '',
      codeVisible: false,
      codeError: ''
    },
    code: {
      code: null,
      services: [],
      price: null,
      percent: null
    },
    paymentMethod: 'cc-method',
    recurringPayment: false,
    recurringPaymentAvailable: false,
    config: {},

    // dane z API
    globalDiscountText: 'Od dziś do końca promocji zniżka 20% na wszystkie plany.',
    isGlobalDiscountLabelShowed: false
  },
  getters: {
    selectedPlan: (state) => {
      if (state.selectedPlanIndex !== null && state.plans.length > 0) {
        return state.plans[state.selectedPlanIndex]
      } else {
        return null
      }
    },
    selectedPeriod: (state, getters) => {
      let plan = getters.selectedPlan
      if (plan !== null && state.selectedPeriodIndex !== null) {
        return plan.periods[state.selectedPeriodIndex]
      } else {
        return null
      }
    },
    selectedPlanName: (state, getters) => {
      let plan = getters.selectedPlan
      if (plan !== null) {
        return plan.name
      } else {
        return ''
      }
    },
    selectedPlanId: (state, getters) => {
      let plan = getters.selectedPlan
      if (plan !== null) {
        return plan.id
      } else {
        return null
      }
    },
    selectedPeriodName: (state, getters) => {
      let period = getters.selectedPeriod
      if (period !== null) {
        return period.name
      } else {
        return ''
      }
    },
    selectedExtras: (state) => {
      let selectedTrainings = state.trainings.filter(t => state.selectedExtrasIds.includes(t.id))
      let selectedOthers = state.others.filter(o => state.selectedExtrasIds.includes(o.id))
      return [...selectedTrainings, ...selectedOthers]
    },
    selectedExtrasCount: (state) => {
      return state.selectedExtrasIds.length
    },
    getAdditionalDays: (state, getters) => {
      return state.additionalDays
    },
    getShowAdditionalDays: (state, getters) => {
      return state.showAdditionalDays
    },
    isVip: (state, getters) => {
      if (state.user.isVip || getters.selectedPlanName === 'VIP') {
        return true
      } else {
        return false
      }
    },
    getSummary: (state, getters) => {
      let taxRate = 23
      let code = state.code
      let usedCodeDiscount = 0
      let unusedCodeDiscount = code.price

      let summary = {
        plan: null,
        extras: [],
        discounts: [],
        packageDurationChange: false,
        taxRate: taxRate,
        totalPriceNet: 0,
        totalTax: 0,
        totalSavings: 0,
        totalPriceGross: 0,
        totalPriceBeforeDiscountNet: 0,
        totalTaxBeforeDiscount: 0,
        totalPriceBeforeDiscountGross: 0,
        totalDiscount: 0
      }

      let totalPriceNet = 0
      let totalTax = 0
      let totalSavings = 0
      let totalPriceGross = 0
      let totalDiscount = 0
      let totalPriceBeforeDiscountNet = 0
      let totalTaxBeforeDiscount = 0
      let totalPriceBeforeDiscountGross = 0

      // Count plan position if it was selected
      let selectedPlan = getters.selectedPlan
      let selectedPeriod = getters.selectedPeriod

      if (selectedPlan !== null && selectedPeriod !== null) {
        let priceNet = selectedPeriod.price
        if (selectedPeriod.pricePromo) {
          priceNet = selectedPeriod.pricePromo
        }
        let tax = roundToTwo(priceNet * taxRate / 100)
        let priceGross = roundToTwo(priceNet + tax)

        summary.plan = {
          name: selectedPlan.name,
          periodName: selectedPeriod.name,
          net: priceNet,
          taxRate: taxRate,
          tax: tax,
          gross: priceGross
        }

        if (code.code !== null && code.services.includes(selectedPeriod.packageId)) {
          if (code.price !== null) {
            if (unusedCodeDiscount > priceGross) {
              usedCodeDiscount += priceGross
              unusedCodeDiscount -= priceGross
            } else {
              usedCodeDiscount += unusedCodeDiscount
              unusedCodeDiscount -= unusedCodeDiscount
            }
          } else if (code.percent !== null) {
            usedCodeDiscount += roundToTwo(priceGross * code.percent / 100)
          }
        }

        totalPriceNet += priceNet
        totalPriceGross += priceGross
        totalTax += tax
        if (selectedPeriod.priceSaveUp) {
          totalSavings += selectedPeriod.priceSaveUp
        }
      }

      // Count extras if they were selected
      getters.selectedExtras.forEach((e) => {
        let priceNet = e.price
        if (e.pricePromoVip !== null && getters.isVip) {
          priceNet = e.pricePromoVip
        } else if (e.pricePromo !== null) {
          priceNet = e.pricePromo
        } else if (e.priceVip !== null && getters.isVip) {
          priceNet = e.priceVip
        }
        let tax = roundToTwo(priceNet * taxRate / 100)
        let priceGross = roundToTwo(priceNet + tax)

        summary.extras.push({
          name: e.name,
          net: priceNet,
          taxRate: taxRate,
          tax: tax,
          gross: priceGross
        })

        if (code.code !== null && code.services.includes(e.id)) {
          if (code.price !== null) {
            if (unusedCodeDiscount > priceGross) {
              usedCodeDiscount += priceGross
              unusedCodeDiscount -= priceGross
            } else {
              usedCodeDiscount += unusedCodeDiscount
              unusedCodeDiscount -= unusedCodeDiscount
            }
          } else if (code.percent !== null) {
            usedCodeDiscount += roundToTwo(priceGross * code.percent / 100)
          }
        }

        totalPriceNet += priceNet
        totalPriceGross += priceGross
        totalTax += tax
      })

      totalPriceBeforeDiscountNet = totalPriceNet
      totalTaxBeforeDiscount = totalTax
      totalPriceBeforeDiscountGross = totalPriceGross

      // Discounts
      let additionalDays = getters.getAdditionalDays
      if (additionalDays > 0) {
        let now = moment()
        let period = getters.selectedPeriod
        let days = period.days + additionalDays
        let planEndDate = now.add(days, 'days').format('DD.MM.YYYY')

        summary.discounts.push({
          name: 'UWAGA! Aktualizacja dostępu! ',
          tooltip: 'Pozostały okres trwania Twojego aktualnego planu zostanie przeliczony na wartość nowego. Dotychczasowy plan może zostać skrócony.',
          extra: 'Twój nowy plan będzie aktywny do ' + planEndDate
        })
        summary.packageDurationChange = true
      }

      if (state.recurringPayment) {
        let discountNet = 2.44
        let discountTax = roundToTwo(discountNet * taxRate / 100)
        let discountGross = discountNet + discountTax

        summary.discounts.push({
          name: 'Zniżka za płatność cykliczną',
          net: discountNet,
          taxRate: taxRate,
          tax: discountTax,
          gross: discountGross
        })
        totalPriceNet -= discountNet
        totalPriceGross -= discountGross
        totalDiscount += discountGross
      }

      if (code.code !== null && usedCodeDiscount > 0) {
        let discountNet = roundToTwo(usedCodeDiscount / ((100 + taxRate) / 100))
        let discountGross = usedCodeDiscount
        let discountTax = discountGross - discountNet
        summary.discounts.push({
          name: 'Kod rabatowy: ' + code.code,
          net: discountNet,
          taxRate: taxRate,
          tax: discountTax,
          gross: discountGross
        })

        totalPriceNet -= discountNet
        totalPriceGross -= discountGross
        totalDiscount += discountGross
      }
      if (totalPriceNet < 0) {
        totalPriceNet = 0
      }
      if (totalPriceGross < 0) {
        totalPriceGross = 0
      }
      summary.totalPriceNet = totalPriceNet
      summary.totalTax = totalTax
      summary.totalSavings = totalSavings
      summary.totalPriceGross = totalPriceGross
      summary.totalDiscount = totalDiscount
      summary.totalPriceBeforeDiscountNet = totalPriceBeforeDiscountNet
      summary.totalTaxBeforeDiscount = totalTaxBeforeDiscount
      summary.totalPriceBeforeDiscountGross = totalPriceBeforeDiscountGross

      return summary
    },
    getTotalPrice: (state, getters) => {
      return getters.getSummary.totalPriceGross
    },
    userInfo: (state) => {
      return {
        'avatar': state.user.avatar,
        'clientFirstName': state.form.clientFirstName,
        'clientLastName': state.form.clientLastName,
        'userFirstName': state.user.userFirstName,
        'userLastName': state.user.userLastName,
        'clientName': state.form.clientFirstName + ' ' + state.form.clientLastName,
        'isDietitian': state.user.isDietitian
      }
    },
    getAllServices: (state, getters) => {
      let services = [ ...state.selectedExtrasIds ]
      if (state.selectedPackageId !== null) {
        services.push(state.selectedPackageId)
      }
      return services
    }
  },
  mutations: {
    setConfig (state, value) {
      state.config = value
    },
    setAdditionalDays (state, value) {
      state.additionalDays = value
    },
    setShowAdditionalDays (state, value) {
      state.showAdditionalDays = value
    },
    setCreatorData (state, creatorData) {
      state.plans = creatorData.plans
      state.trainings = creatorData.trainings
      state.others = creatorData.others

      if (state.plans.length > 0) {
        let selectedPlanIndex = state.plans.length - 1
        state.plans.forEach(function (plan, index) {
          if (plan.name === 'VIP') {
            selectedPlanIndex = index
          }
        })
        state.selectedPlanIndex = selectedPlanIndex
      }
      state.loadingCreatorData = false
    },
    setUserData (state, userData) {
      state.user.userFirstName = userData.first_name
      state.user.userLastName = userData.last_name
      state.user.email = userData.email
      state.user.planName = userData.current_plan_name
      state.user.packageUnusedAmount = userData.current_user_package_unused_amount
      state.user.currentPeriodId = userData.current_user_package_period_id
      state.user.currentPlanId = userData.current_user_package_plan_id
      state.user.disabledExtras = userData.disabled_extras
      state.user.isVip = userData.is_vip
      state.user.isDietitian = userData.is_dietitian
      if (userData.avatar) {
        state.user.avatar = userData.avatar
      } else {
        state.user.avatar = require('./assets/img/payment_sidebar/default_avatar.png')
      }
      state.form.clientFirstName = userData.first_name
      state.form.clientLastName = userData.last_name
      state.form.clientPhone = userData.document_phone
      state.form.documentName = userData.document_name
      state.form.documentStreet = userData.document_street
      state.form.documentPostalCode = userData.document_postal_code
      state.form.documentCity = userData.document_city
      state.form.documentVatId = userData.document_vat_id
      if (userData.document_type === 1) {
        state.invoice = false
      } else {
        state.invoice = true
      }
      Sentry.configureScope(scope => {
        scope.setExtra('current_plan_name', state.user.planName)
        scope.setExtra('is_vip', state.user.isVip)
        scope.setUser({
          'email': state.user.email
        })
      })
      state.loadingUserData = false
    },
    selectPlan (state, planIndex) {
      state.selectedPlanIndex = planIndex
    },
    selectPeriod (state, periodIndex) {
      state.selectedPeriodIndex = periodIndex
      if (periodIndex !== null) {
        state.selectedPackageId = state.plans[state.selectedPlanIndex].periods[state.selectedPeriodIndex].packageId
      } else {
        state.selectedPackageId = null
      }
    },
    selectExtra (state, extraId) {
      const selectedExtrasIds = [ ...state.selectedExtrasIds ]
      selectedExtrasIds.push(extraId)
      state.selectedExtrasIds = selectedExtrasIds
    },
    deselectExtra (state, extraId) {
      state.selectedExtrasIds.splice(state.selectedExtrasIds.indexOf(extraId), 1)
    },
    deselectAllExtras (state) {
      state.selectedExtrasIds = []
    },
    setStep (state, step) {
      state.step = step
    },
    setFormValue (state, formData) {
      state.form[formData[0]] = formData[1]
    },
    setInvoice (state, status) {
      state.invoice = status
    },
    setVatIdNotRequired (state, status) {
      state.vatIdNotRequired = status
    },
    setPaymentMethod (state, method) {
      state.paymentMethod = method
    },
    setRecurringPayment (state, status) {
      state.recurringPayment = status
    },
    setRecurringPaymentAvailable (state, status) {
      state.recurringPaymentAvailable = status
      if (status === false) {
        state.recurringPayment = false
      }
    },
    setCode (state, data) {
      state.code.code = data.code
      state.code.services = data.services
      state.code.price = data.price
      state.code.percent = data.percent
    },
    setCodeError (state, error) {
      state.form.codeError = error
    },
    setSendingOrder (state, status) {
      state.sendingOrder = status
    }
  },
  actions: {
    fetchConfig ({ commit }) {
      const config = {
        headers: {
          'Cache-Control': 'no-cache'
        }
      }
      let url = process.env.BASE_URL + 'config.json'
      axios.get(url, config)
        .then(function (response) {
          commit('setConfig', response.data)
        })
    },
    fetchCreatorData ({ commit }) {
      let url = process.env.VUE_APP_PAYMENTS_SERVICE_HOST + process.env.VUE_APP_PAYMENTS_SERVICE_INITIAL_DATA_PATH
      axios.get(url)
        .then(function (response) {
          commit('setCreatorData', response.data)
        })
    },
    fetchUserData ({ commit, state }) {
      if (state.token !== '') {
        let url = process.env.VUE_APP_AUTH_HOST + process.env.VUE_APP_AUTH_USER_PATH
        url += auth.getId() + '/'
        let config = {
          headers: { 'Authorization': 'Bearer ' + auth.getToken() }
        }
        axios.get(url, config)
          .then(function (response) {
            commit('setUserData', response.data)
          })
      }
    },
    fetchAdditionalDays ({ commit, state }) {
      if (state.selectedPackageId === null) {
        commit('setAdditionalDays', 0)
      } else {
        const data = {
          email: state.user.email,
          selected_package_id: state.selectedPackageId.replace('package-', '')
        }
        const config = {
          headers: { 'Authorization': 'Bearer ' + auth.getToken() }
        }
        let url = process.env.VUE_APP_AUTH_HOST + process.env.VUE_APP_AUTH_PACKAGE_COUNT_ADDITIONAL_DAYS_URL
        axios.post(url, data, config)
          .then(function (response) {
            commit('setShowAdditionalDays', true)
            commit('setAdditionalDays', response.data)
          })
          .catch(function () {
            commit('setShowAdditionalDays', false)
          })
      }
    },
    updateRecurringPayment ({ commit, state, getters }) {
      if (state.selectedPlanIndex !== null && state.selectedPeriodIndex === 0 && getters.selectedExtrasCount === 0 && state.paymentMethod === 'cc-method' && state.code.code === null) {
        commit('setRecurringPaymentAvailable', true)
      } else {
        commit('setRecurringPaymentAvailable', false)
      }
    },
    checkCode ({ commit, state }) {
      return new Promise((resolve, reject) => {
        if (!state.form.code) {
          resolve()
          return
        }
        let url = process.env.VUE_APP_PAYMENTS_SERVICE_HOST + process.env.VUE_APP_PAYMENTS_SERVICE_CHECK_CODE_PATH
        axios.post(url, {
          code: state.form.code
        }).then((response) => {
          let services = response.data.services
          let noElementsToDiscount = true
          if (services.includes(state.selectedPackageId)) {
            noElementsToDiscount = false
          }
          state.selectedExtrasIds.forEach((obj) => {
            if (services.includes(obj)) {
              noElementsToDiscount = false
            }
          })

          Sentry.captureException(new Error('code...'))
          commit('setCode', response.data)
          commit('setCodeError', '')
          if (noElementsToDiscount) {
            commit('setCodeError', 'Kod zniżkowy nie rabatuje żadnej z wybranych usług')
            Sentry.withScope(scope => {
              scope.setExtra('code', state.form.code)
              Sentry.captureException(new Error('LEVEL_LOW zniżkowy nie rabatuje żadnej z wybranych usług'))
            })
            reject(new Error('Kod zniżkowy nie rabatuje żadnej z wybranych usług'))
          }
          resolve()
        }).catch((error) => {
          commit('setCode', {
            code: null,
            services: [],
            price: null,
            percent: null
          })
          let errorLevel = 'LEVEL_LOW'
          let errorMsg = error.toLocaleString()
          if (!error.response) {
            errorLevel = 'LEVEL_MEDIUM' // "Network Error"
          }
          if (error.response) {
            errorMsg = error.response.data.detail
            if (error.response.status !== 400) {
              errorLevel = 'LEVEL_MEDIUM' + error.response.status // 500 and others
            }
          }
          commit('setCodeError', errorMsg)
          if (errorLevel === 'LEVEL_MEDIUM') {
            Sentry.withScope(scope => {
              scope.setExtra('code', state.form.code)
              Sentry.captureException(new Error(errorLevel + ': ' + errorMsg))
            })
          }
        })
      })
    }
  }
})

store.watch((state) => state.selectedPackageId, () => {
  store.dispatch('fetchAdditionalDays')
})

store.watch((state) => state.token, () => {
  store.dispatch('fetchUserData')
})

let updateRecurringPayment = function () {
  store.dispatch('updateRecurringPayment')
}

store.watch((state) => state.selectedPlanIndex, updateRecurringPayment)
store.watch((state) => state.selectedPeriodIndex, updateRecurringPayment)
store.watch((state) => state.selectedExtrasIds, updateRecurringPayment)
store.watch((state) => state.paymentMethod, updateRecurringPayment)
store.watch((state) => state.code.code, updateRecurringPayment)

export default store
